/**
 * Summary Table - Shows computed (cheapest/priority) rates per route and DESI
 */

import React from 'react';
import { X } from 'lucide-react';
import type { ComputedShippingRate, CustomShippingRoute } from '../../types/shipping';
import { getCurrencySymbol } from '../../utils/currency';

interface SummaryTableProps {
  routes: CustomShippingRoute[];
  visibleDesi: number[];
  exchangeRates: Record<string, number>;
  deleteRouteConfirm: string | null;
  setDeleteRouteConfirm: (id: string | null) => void;
  getComputedRate: (routeId: string, desi: number) => ComputedShippingRate | null;
}

const SummaryTable: React.FC<SummaryTableProps> = ({
  routes,
  visibleDesi,
  exchangeRates,
  getComputedRate,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white">
        <h3 className="font-semibold">{'\u00D6'}zet Tablo (En Ucuz / {'\u00D6'}ncelikli)</h3>
        <p className="text-sm opacity-90">Rotalara g{'\u00F6'}re otomatik se{'\u00E7'}ilen fiyatlar {'\u2022'} {routes.length} rota aktif</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="sticky left-0 bg-slate-50 px-4 py-2 text-left font-semibold text-slate-700 min-w-[80px] z-10">
                DESI
              </th>
              {routes.map(route => (
                <th key={route.routeId} className="px-2 py-2 text-center min-w-[85px]">
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{route.flag}</span>
                    <span className="font-semibold text-slate-700 text-xs">{route.shortName}</span>
                    {!route.isDefault && (
                      <button
                        onClick={() => {/* handled by parent via deleteRouteConfirm */}}
                        className="text-red-400 hover:text-red-600 p-0.5"
                        title="Rotay&#x131; sil"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleDesi.map((desi, idx) => (
              <tr
                key={desi}
                className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
              >
                <td className="sticky left-0 bg-inherit px-4 py-2 font-medium text-slate-700 z-10">
                  {desi}
                </td>
                {routes.map(route => {
                  const computed = getComputedRate(route.routeId, desi);
                  const currencySymbol = computed ? getCurrencySymbol(computed.currency) : '';
                  const usdValue = computed ? (
                    computed.currency === 'USD' ? computed.rate :
                    computed.rate * (exchangeRates[computed.currency] || 1)
                  ) : 0;
                  const isNonUSD = computed && computed.currency !== 'USD';
                  return (
                    <td key={route.routeId} className="px-2 py-2 text-center">
                      {computed ? (
                        <div className="flex flex-col items-center">
                          <span className="font-medium text-slate-800">
                            {currencySymbol}{computed.rate.toFixed(2)}
                          </span>
                          {isNonUSD && (
                            <span className="text-xs text-slate-400">
                              ${usdValue.toFixed(2)}
                            </span>
                          )}
                          <span className={`text-xs ${
                            computed.selectionReason === 'priority'
                              ? 'text-amber-600'
                              : 'text-emerald-600'
                          }`}>
                            {computed.carrierName}
                            {computed.selectionReason === 'priority' && ' \u2B50'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryTable;
